'use client';

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import React, { useEffect, useMemo, useRef } from 'react';
import makeCancellable from 'make-cancellable-promise';
import clsx from 'clsx';
import invariant from 'tiny-invariant';
import warning from 'warning';
import pdfjs from "../pdfjs.js";
import useDocumentContext from "../shared/hooks/useDocumentContext.js";
import usePageContext from "../shared/hooks/usePageContext.js";
import useResolver from "../shared/hooks/useResolver.js";
import { cancelRunningTask } from "../shared/utils.js";
export default function AnnotationLayer() {
  var documentContext = useDocumentContext();
  var pageContext = usePageContext();
  invariant(pageContext, 'Unable to find Page context.');
  var mergedProps = Object.assign(Object.assign({}, documentContext), pageContext);
  var imageResourcesPath = mergedProps.imageResourcesPath,
    linkService = mergedProps.linkService,
    onGetAnnotationsErrorProps = mergedProps.onGetAnnotationsError,
    onGetAnnotationsSuccessProps = mergedProps.onGetAnnotationsSuccess,
    onRenderAnnotationLayerErrorProps = mergedProps.onRenderAnnotationLayerError,
    onRenderAnnotationLayerSuccessProps = mergedProps.onRenderAnnotationLayerSuccess,
    page = mergedProps.page,
    pdf = mergedProps.pdf,
    renderForms = mergedProps.renderForms,
    rotate = mergedProps.rotate,
    _mergedProps$scale = mergedProps.scale,
    scale = _mergedProps$scale === void 0 ? 1 : _mergedProps$scale;
  invariant(pdf, 'Attempted to load page annotations, but no document was specified. Wrap <Page /> in a <Document /> or pass explicit `pdf` prop.');
  invariant(page, 'Attempted to load page annotations, but no page was specified.');
  invariant(linkService, 'Attempted to load page annotations, but no linkService was specified.');
  var _useResolver = useResolver(),
    _useResolver2 = _slicedToArray(_useResolver, 2),
    annotationsState = _useResolver2[0],
    annotationsDispatch = _useResolver2[1];
  var annotations = annotationsState.value,
    annotationsError = annotationsState.error;
  var layerElement = useRef(null);
  warning(parseInt(window.getComputedStyle(document.body).getPropertyValue('--react-pdf-annotation-layer'), 10) === 1, 'AnnotationLayer styles not found. Read more: https://github.com/wojtekmaj/react-pdf#support-for-annotations');
  function onLoadSuccess() {
    if (!annotations) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    if (onGetAnnotationsSuccessProps) {
      onGetAnnotationsSuccessProps(annotations);
    }
  }
  function onLoadError() {
    if (!annotationsError) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    warning(false, annotationsError.toString());
    if (onGetAnnotationsErrorProps) {
      onGetAnnotationsErrorProps(annotationsError);
    }
  }
  function resetAnnotations() {
    annotationsDispatch({
      type: 'RESET'
    });
  }
  useEffect(resetAnnotations, [annotationsDispatch, page]);
  function loadAnnotations() {
    if (!page) {
      return;
    }
    var cancellable = makeCancellable(page.getAnnotations());
    var runningTask = cancellable;
    cancellable.promise.then(function (nextAnnotations) {
      annotationsDispatch({
        type: 'RESOLVE',
        value: nextAnnotations
      });
    }).catch(function (error) {
      annotationsDispatch({
        type: 'REJECT',
        error: error
      });
    });
    return function () {
      cancelRunningTask(runningTask);
    };
  }
  useEffect(loadAnnotations, [annotationsDispatch, page, renderForms]);
  useEffect(function () {
    if (annotations === undefined) {
      return;
    }
    if (annotations === false) {
      onLoadError();
      return;
    }
    onLoadSuccess();
  },
  // Ommitted callbacks so they are not called every time they change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [annotations]);
  function onRenderSuccess() {
    if (onRenderAnnotationLayerSuccessProps) {
      onRenderAnnotationLayerSuccessProps();
    }
  }
  function onRenderError(error) {
    warning(false, "".concat(error));
    if (onRenderAnnotationLayerErrorProps) {
      onRenderAnnotationLayerErrorProps(error);
    }
  }
  var viewport = useMemo(function () {
    return page.getViewport({
      scale: scale,
      rotation: rotate
    });
  }, [page, rotate, scale]);
  function renderAnnotationLayer() {
    if (!pdf || !page || !linkService || !annotations) {
      return;
    }
    var layer = layerElement.current;
    if (!layer) {
      return;
    }
    var clonedViewport = viewport.clone({
      dontFlip: true
    });
    var annotationLayerParameters = {
      accessibilityManager: null,
      // TODO: Implement this
      annotationCanvasMap: null,
      // TODO: Implement this
      div: layer,
      l10n: null,
      // TODO: Implement this
      page: page,
      viewport: clonedViewport
    };
    var renderParameters = {
      annotations: annotations,
      annotationStorage: pdf.annotationStorage,
      div: layer,
      // See https://github.com/mozilla/pdf.js/issues/17029
      downloadManager: null,
      imageResourcesPath: imageResourcesPath,
      linkService: linkService,
      page: page,
      renderForms: renderForms,
      viewport: clonedViewport
    };
    layer.innerHTML = '';
    try {
      new pdfjs.AnnotationLayer(annotationLayerParameters).render(renderParameters);
      // Intentional immediate callback
      onRenderSuccess();
    } catch (error) {
      onRenderError(error);
    }
    return function () {
      // TODO: Cancel running task?
    };
  }
  useEffect(renderAnnotationLayer,
  // Ommitted callbacks so they are not called every time they change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [annotations, imageResourcesPath, linkService, page, renderForms, viewport]);
  return /*#__PURE__*/React.createElement("div", {
    className: clsx('react-pdf__Page__annotations', 'annotationLayer'),
    ref: layerElement
  });
}